import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CardContent from '@material-ui/core/CardContent';
import CardMedia from '@material-ui/core/CardMedia';
import Typography from '@material-ui/core/Typography';
import Dialog from '@material-ui/core/Dialog';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { withStyles } from '@material-ui/core/styles';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import { BsArrowLeft, BsArrowRight } from "react-icons/bs";
import './styles.css';
import { CircularProgress } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import HideImageOutlinedIcon from '@mui/icons-material/HideImageOutlined';

const useStyles = makeStyles((theme) => ({
  icon: {
    color: 'white',
    border: '2px solid black',
    padding: '2px',
  },
  imageContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  root: {
    position: 'relative',
    width: '100%',
    height: '99.5%',
    maxWidth: '100%',
    backgroundColor: '#F5F5F5'
  },
  navButton: {
    top: '0px',
    color: '#FFFFFF',
    zIndex: 2,
  },
  viewButton: {
    bottom: '10px',
    right: '10px',
    backgroundColor: '#FFFFFF',
    color: '#000000',
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  leftButton: {
    left: '10px',
  },
  rightButton: {
    right: '10px',
  },
  textDescriptionInfo: {
    color: '#000',
    fontWeight: 400,
    fontFamily: 'Rawline',
  },
  buttonImage: {
    color: '#155BCB',
    fontFamily: 'Rawline',
    padding: 0
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  DialogImage: {
    width: 1400
  },
  message: {
    padding: 10,
    backgroundColor: '#FFFBE8',
    color: 'rgba(0,0,0,0.8)',
    borderRadius: 7,
    width: '100%',
    fontFamily: 'Rawline'
  },
  textTitle: {
    color: '#103168',
    fontWeight: 600,
    textAlign: 'center',
    marginBottom: 20
  },
  imageIndicator: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    bottom: 10,
    width: '100%',
  },
  dot: {
    padding: 4,
    marginRight: 5,
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    cursor: 'pointer',
  },
  activeDot: {
    backgroundColor: '#FFFFFF',
  },
}));

const styles = (theme) => ({
  root: {
    padding: theme.spacing(2),
    backgroundColor: '#F5F5F5'
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
});

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(0),
  },
}))(MuiDialogContent);

const MEDIA_HEIGHT = 200;

// Estado único para quando não há mídia, ela falhou ao carregar (URL expirada,
// NoSuchKey/404 no MinIO) ou o formato não é suportado.
const UnavailableMedia = ({ height = MEDIA_HEIGHT }) => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height,
    width: '100%',
    color: '#103168',
    fontFamily: 'Rawline',
  }}>
    <HideImageOutlinedIcon style={{ fontSize: 48, marginBottom: 8 }} />
    <span style={{ fontSize: 16 }}>Mídia indisponível</span>
  </div>
);

// Loader sempre centralizado e com a mesma altura da mídia, para não "pular"
// de posição entre os estados (carregando dados -> carregando imagem -> imagem).
const Loader = ({ height = MEDIA_HEIGHT }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height,
    width: '100%',
    backgroundColor: '#F5F5F5',
  }}>
    <CircularProgress />
  </div>
);

export default function MidiaComplaint({ description, midias, isLoading, executorComment }) {
  const classes = useStyles();

  const [open, setOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  // Guarda os índices cuja mídia falhou ao carregar (ex.: URL expirada, objeto
  // inexistente no MinIO -> NoSuchKey/404). Nesses casos exibimos "Mídia indisponível".
  const [failedIndexes, setFailedIndexes] = useState({});
  // Índices cuja imagem já terminou de carregar; enquanto não carrega, mostramos
  // um placeholder de altura fixa para não quebrar/pular o layout.
  const [loadedIndexes, setLoadedIndexes] = useState({});

  useEffect(() => {
    setCurrentImageIndex(0);
    setFailedIndexes({});
    setLoadedIndexes({});
  }, [midias]);

  const handleMediaError = () => {
    setFailedIndexes((prev) => ({ ...prev, [currentImageIndex]: true }));
  };

  const handleMediaLoad = () => {
    setLoadedIndexes((prev) => ({ ...prev, [currentImageIndex]: true }));
  };

  const handleClickOpen = () => {
    const video = document.getElementById('video_midia');
    if (video) video.pause();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleNextImage = () => {
    if (currentImageIndex < midias.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    }
  };

  const handlePreviousImage = () => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    }
  };

  const navigateToImage = (index) => {
    setCurrentImageIndex(index);
  };

  const url = midias[currentImageIndex]?.url || '';
  const type = midias[currentImageIndex]?.type || '';

  const hasFailed = failedIndexes[currentImageIndex];

  const isVideo =
    !hasFailed && (
      type.startsWith('video/') ||
      /\.(mp4|webm|ogg|mov)(\?|$)/i.test(url) ||
      ['mp4', 'webm', 'ogg', 'mov'].includes(type.toLowerCase()));

  // Por padrão tratamos como imagem (tentando carregar e mostrando o loader).
  // O "Mídia indisponível" só aparece de fato quando o carregamento falha
  // (onError -> hasFailed), evitando o flash quando o type/url ainda não foi
  // reconhecido. URLs assinadas (MinIO) terminam em "?...", então a extensão
  // nem sempre casa com regex — por isso não dependemos só dela.
  const isImage = !hasFailed && !isVideo;

  // Mostra o loader enquanto busca as URLs (isLoading) ou enquanto a imagem
  // atual ainda não terminou de carregar. Mantém um loader contínuo.
  const showLoader =
    isLoading ||
    (midias.length > 0 && isImage && !loadedIndexes[currentImageIndex]);

  return (
    <Card className={classes.root}>
      <CardContent>
        <Typography variant="h6" align="center" className={classes.textTitle}>
          {isImage ? "Foto" : isVideo ? "Vídeo" : "Mídia"} da ocorrência
        </Typography>
        <div className={classes.message}>
          Abaixo é possível visualizar a imagem ou vídeo relacionado à ocorrência e a descrição registrada pelo solicitante.
        </div>
      </CardContent>
      <div style={{ position: 'relative' }}>
        {!isLoading && midias.length === 0 ? (
          <UnavailableMedia />
        ) : midias.length > 0 ? (
          <>
            {/* Setas de navegação só aparecem quando a mídia já está visível,
                para não ficarem sobre o loader. */}
            {!showLoader && (
              <div style={{
                position: 'absolute',
                height: '100%',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                pointerEvents: 'none',
                zIndex: 1,
              }}>
                <IconButton style={{ pointerEvents: 'auto' }} className={`${classes.navButton} ${classes.leftButton}`} onClick={handlePreviousImage}>
                  <BsArrowLeft />
                </IconButton>
                <IconButton style={{ pointerEvents: 'auto' }} className={`${classes.navButton} ${classes.rightButton}`} onClick={handleNextImage}>
                  <BsArrowRight />
                </IconButton>
              </div>
            )}
            <div style={{ textAlign: "right" }}>
              {isImage ? (
                // A imagem fica sempre montada (escondida) para disparar
                // onLoad/onError; só aparece após carregar, sem efeito visual.
                <CardMedia
                  component="img"
                  alt="Image Slider"
                  image={midias[currentImageIndex]?.url}
                  title="Image Slider"
                  onError={handleMediaError}
                  onLoad={handleMediaLoad}
                  style={{
                    width: '100%',
                    height: MEDIA_HEIGHT,
                    objectFit: 'cover',
                    visibility: loadedIndexes[currentImageIndex] ? 'visible' : 'hidden',
                  }}
                />
              ) : isVideo ? (
                <video
                  key={currentImageIndex}
                  id="video_midia"
                  controls
                  style={{ minWidth: '100%', maxHeight: MEDIA_HEIGHT }}
                  src={midias[currentImageIndex]?.url}
                  onError={handleMediaError}
                />
              ) : (
                <UnavailableMedia />
              )}
              {(isImage || isVideo) && !showLoader && (
                <IconButton
                  style={{ marginTop: -100, marginRight: 10 }}
                  onClick={handleClickOpen}
                  className={`${classes.navButton} ${classes.navButton}`}
                >
                  <FullscreenIcon style={{ fontSize: '25px' }} />
                </IconButton>
              )}
            </div>
          </>
        ) : null}

        {/* Loader único e persistente: cobre tanto a busca das URLs (isLoading)
            quanto o carregamento da imagem atual, sem piscar dois loaders. */}
        {showLoader && (
          <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', zIndex: 2 }}>
            <Loader />
          </div>
        )}
        <div className={classes.imageIndicator}>
          {midias.map((midia, index) => (
            <div
              key={index}
              className={`${classes.dot} ${index === currentImageIndex ? classes.activeDot : ''}`}
              onClick={() => navigateToImage(index)}
            />
          ))}
        </div>
      </div>
      <CardContent>
        <Typography variant="body2" style={{ padding: '10px' }}>
          Descrição da ocorrência:<br />
          {description}
        </Typography>
        {executorComment?.description && (
          <Typography variant="body2" style={{ padding: '10px' }}>
            Anotação do executor:<br />
            {executorComment.description}
          </Typography>
        )}
      </CardContent>
      {midias.length > 0 && (
        <div style={{ marginBottom: 20, marginLeft: 12, position: 'absolute', bottom: 0 }}>
          <CardActions>
            <Dialog maxWidth='lg' onClose={handleClose} open={open}>
              <DialogTitle onClose={handleClose}>
                Mídia Completa
              </DialogTitle>
              <DialogContent>
                {isImage ? (
                  <img
                    alt="Foto da ocorrência"
                    src={midias[currentImageIndex]?.url}
                    onError={handleMediaError}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%"
                    }}
                  />
                ) : isVideo ? (
                  <video
                    id="video_midia_full"
                    controls
                    style={{ maxWidth: "100%", maxHeight: "100%" }}
                    src={midias[currentImageIndex]?.url}
                    onError={handleMediaError}
                  />
                ) : (
                  <UnavailableMedia height={300} />
                )}
              </DialogContent>
            </Dialog>
          </CardActions>
        </div>
      )}
    </Card>
  );
}